import type { SsoError } from '../contract';

// The browser's side of signing in with Google or Apple. Pure (the storage and the clock are passed in), so it can be tested without a screen.
//
// The browser makes a random secret, keeps it for the length of the sign-in (in this tab only), and tells the API just its hash. At the
// end, the one-time code in the address is worth nothing without the secret, so a code that leaks, or one made by someone else's sign-in, cannot
// be turned into a session here (see the API contract, auth.ts).

const KEY = 'ssoVerifier';

const base64Url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const sha256Base64Url = async (text: string) => base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))));

// A new secret (43 URL-safe characters: 32 random bytes) and the hash to give the API.
export async function makeBrowserSecret() {
    const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
    return { verifier, challenge: await sha256Base64Url(verifier) };
}

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;

// Kept in the browser's ordinary storage, not per tab: an installed app (a PWA) can come back from the provider in a fresh window. It
// is only good for as long as a sign-in takes, and it is forgotten as it is read.
const KEEP_MS = 15 * 60 * 1000;

export const rememberVerifier = (verifier: string, storage: Storage = localStorage, now = Date.now()) =>
    storage.setItem(KEY, JSON.stringify({ verifier, at: now }));

// The secret kept for this sign-in, which is forgotten as it is read: it is good for one try. Null when there is none, or it is too old.
export function takeVerifier(storage: Storage = localStorage, now = Date.now()): string | null {
    const kept = storage.getItem(KEY);
    storage.removeItem(KEY);
    if (!kept) return null;

    try {
        const { verifier, at } = JSON.parse(kept);
        return typeof verifier === 'string' && typeof at === 'number' && now - at <= KEEP_MS ? verifier : null;
    } catch {
        return null;
    }
}

export const providerName = (provider: string) => (provider === 'google' ? 'Google' : provider === 'apple' ? 'Apple' : provider);

export const ssoCodeFromSearch = (search: string) => new URLSearchParams(search).get('code') ?? '';

// Why the trip to a provider ended in failure, from the address of the login page (?sso_error=...), or null when it did not.
export function ssoErrorFromSearch(search: string): SsoError | null {
    const value = new URLSearchParams(search).get('sso_error');
    if (!value) return null;

    // A reason this version does not know (the address can say anything) is shown as a plain failure.
    return (SSO_ERRORS as readonly string[]).includes(value) ? value as SsoError : 'failed';
}

const SSO_ERRORS = ['cancelled', 'expired', 'failed', 'email_not_verified', 'limit', 'unavailable'] as const;

export const SSO_ERROR_MESSAGES: Record<SsoError, string> = {
    cancelled: 'Sign-in was cancelled.',
    expired: 'That sign-in took too long or was already used. Please try again.',
    failed: 'We could not sign you in that way. Please try again, or use your email and password.',
    email_not_verified: 'That account has an email address that has not been verified, so we cannot use it. Verify it with the provider, or use another way to sign in.',
    limit: 'Too many new accounts have been made today. Please try again tomorrow.',
    unavailable: 'Sign-in is not working right now. Please try again in a moment.',
};

// How the account signs in, in a sentence for the profile page; null for a temporary account (it has no real way to sign in again).
export function signInSummary(account: { temporary?: boolean, hasPassword?: boolean, signInProviders?: string[] }): string | null {
    if (account.temporary) return null;

    const providers = (account.signInProviders ?? []).map(providerName);
    const via = providers.length ? `Signs in with ${providers.join(' and ')}${account.hasPassword === false ? '' : ' or a password'}.` : null;
    if (account.hasPassword === false) return `${via ?? 'Signs in without a password.'} No password is set; to add one, use "Forgot your password?" on the login page.`;

    return via;
}
